import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/organisms/Layout";
import Payment from "../../../api/Payment";
import PaymentDetails from "../../../api/PaymentDetails";
import Cards from "../../../api/Cards";
import Location from "../../../api/Location";

const StripeDetails = () => {
  const { t } = useTranslation();
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [stripeDetails, setStripeDetails] = useState({
    customer: null,
    customerId: null,
    cards: [],
    subscriptions: [],
    invoices: [],
  });
  const [showStripeWidgets, setShowStripeWidgets] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [hiringPlans, setHiringPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    document.title = "HeyHire Portal - Stripe Details";
    if (locationId) {
      loadLocationAndStripeDetails();
    }
  }, [locationId]);

  const loadLocationAndStripeDetails = async () => {
    setLoading(true);
    try {
      // First get the location details
      const locationResponse = await Location.getById(locationId);
      const locationData = locationResponse.data?.data || locationResponse.data;

      if (!locationData) {
        toast.error("Location not found");
        navigate("/admin/stripe");
        return;
      }

      setLocation(locationData);

      // Now get Stripe details for this location
      await loadStripeDetails(locationData);
    } catch (error) {
      console.error("Error loading location:", error);
      toast.error(t("common.something-went-wrong"));
      navigate("/admin/stripe");
    } finally {
      setLoading(false);
    }
  };

  const loadStripeDetails = async (locationData) => {
    if (!locationData?.company?.id || !locationData?.id) {
      toast.error("Missing location or company information");
      return;
    }

    try {
      // Get Stripe customer ID for this location
      const customerResponse = await Payment.getStripeCustomerID(
        locationData.company.id,
        locationData.id
      );

      const customerId =
        customerResponse?.data?.stripe_customer?.stripe_customer_id;

      // Extract subscription ID from the customer response
      const stripeSubscriptionId =
        customerResponse?.data?.stripe_customer_details?.subscriptions
          ?.data?.[0]?.id;

      if (!customerId) {
        setStripeDetails({
          customer: null,
          customerId: null,
          cards: [],
          subscriptions: [],
          invoices: [],
        });
        return;
      }

      // Load detailed Stripe information in parallel
      const [customerRes, cardsRes, subscriptionRes, invoicesRes] =
        await Promise.allSettled([
          PaymentDetails.getCustomerById(customerId),
          Cards.getCardByCustomerID(customerId),
          Payment.getSubscriptionDetailsFromCustomerId(customerId),
          Payment.getPayout(locationData.company.id, locationData.id),
        ]);

      // Process subscription data - it's a single object, not an array
      let processedSubscriptions = [];
      if (
        subscriptionRes.status === "fulfilled" &&
        subscriptionRes.value?.data
      ) {
        const subData = subscriptionRes.value.data;
        // The API returns a single subscription object with specific fields
        if (subData && typeof subData === "object" && subData.currentPlan) {
          // Add the stripe subscription ID to the processed subscription data
          processedSubscriptions = [
            {
              ...subData,
              subscription_id: stripeSubscriptionId || subData.subscription_id,
            },
          ];
        }
      }

      // Process cards data from the API response
      let processedCards = [];
      if (cardsRes.status === "fulfilled" && cardsRes.value?.data) {
        const cardData = cardsRes.value.data;
        if (Array.isArray(cardData["credit-cards"])) {
          processedCards = cardData["credit-cards"];
        }
      }

      setStripeDetails({
        customer:
          customerRes.status === "fulfilled" ? customerRes.value.data : null,
        customerId: customerId,
        cards: processedCards,
        subscriptions: processedSubscriptions,
        invoices:
          invoicesRes.status === "fulfilled"
            ? invoicesRes.value.data?.data || []
            : [],
      });
    } catch (error) {
      console.error("Error loading Stripe details:", error);
      toast.error(t("common.something-went-wrong"));
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // Handle subscription management
  const handleManageSubscription = (subscription) => {
    if (stripeDetails.customerId) {
      // Open billing portal for subscription management
      openBillingPortalForManagement();
    } else {
      toast.error("Unable to manage subscription at this time");
    }
  };

  const openBillingPortalForManagement = async () => {
    try {
      let finalCustomerId = stripeDetails.customerId;

      if (!finalCustomerId && location?.company?.id && location?.id) {
        try {
          const authResponse = await Payment.getStripeCustomerID(
            location.company.id,
            location.id
          );
          finalCustomerId =
            authResponse?.data?.stripe_customer?.stripe_customer_id;
        } catch (fetchError) {
          console.log("Could not fetch customer ID:", fetchError);
        }
      }

      if (!finalCustomerId) {
        toast.error(
          "Unable to retrieve billing information. Please contact support."
        );
        return;
      }

      const response = await Payment.createBillingPortalSession(
        finalCustomerId
      );

      if (response.data.success && response.data.login_url) {
        window.open(response.data.login_url, "_blank", "noopener,noreferrer");
        toast.success("Opening Stripe Billing Portal...");
      } else {
        toast.error(
          response.data.message || "Failed to get billing portal URL"
        );
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      toast.error(`Failed to connect to billing service: ${error.message}`);
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = (invoice) => {
    if (invoice.invoice_pdf) {
      // Open PDF in new tab
      window.open(invoice.invoice_pdf, "_blank", "noopener,noreferrer");
      toast.success("Opening invoice PDF...");
    } else if (invoice.hosted_invoice_url) {
      // Open hosted invoice page
      window.open(invoice.hosted_invoice_url, "_blank", "noopener,noreferrer");
      toast.success("Opening invoice page...");
    } else {
      toast.error("Invoice download not available");
    }
  };

  // Handle payment method updates - Opens Stripe Billing Portal
  const handleUpdatePaymentMethod = async () => {
    try {
      let finalCustomerId = stripeDetails.customerId;

      if (!finalCustomerId && location?.company?.id && location?.id) {
        try {
          const authResponse = await Payment.getStripeCustomerID(
            location.company.id,
            location.id
          );
          finalCustomerId =
            authResponse?.data?.stripe_customer?.stripe_customer_id;
        } catch (fetchError) {
          console.log("Could not fetch customer ID:", fetchError);
        }
      }

      if (!finalCustomerId) {
        toast.error(
          "Unable to retrieve billing information. Please contact support."
        );
        return;
      }

      const response = await Payment.createBillingPortalSession(
        finalCustomerId
      );

      if (response.data.success && response.data.login_url) {
        window.open(response.data.login_url, "_blank", "noopener,noreferrer");
        toast.success("Opening Stripe Payment Portal...");
      } else {
        toast.error(response.data.message || "Failed to open payment portal");
      }
    } catch (error) {
      console.error("Error opening payment portal:", error);
      toast.error(`Failed to connect to payment service: ${error.message}`);
    }
  };

  // Handle adding new payment method
  const handleAddPaymentMethod = async () => {
    await handleUpdatePaymentMethod();
  };

  // Handle removing payment method - Opens Stripe Billing Portal
  const handleRemovePaymentMethod = async (card) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to remove the payment method ending in ${card.card?.last4}? This action will be completed in the Stripe Billing Portal.`
    );

    if (confirmed) {
      await handleUpdatePaymentMethod();
    }
  };

  // Handle modify plan - Opens Stripe Billing Portal
  const handleModifyPlan = async (subscription) => {
    try {
      let finalCustomerId = stripeDetails.customerId;

      if (!finalCustomerId && location?.company?.id && location?.id) {
        try {
          const authResponse = await Payment.getStripeCustomerID(
            location.company.id,
            location.id
          );
          finalCustomerId =
            authResponse?.data?.stripe_customer?.stripe_customer_id;
        } catch (fetchError) {
          console.log("Could not fetch customer ID:", fetchError);
        }
      }

      if (!finalCustomerId) {
        toast.error(
          "Unable to retrieve billing information. Please contact support."
        );
        return;
      }

      const response = await Payment.createBillingPortalSession(
        finalCustomerId
      );

      if (response.data.success && response.data.login_url) {
        window.open(response.data.login_url, "_blank", "noopener,noreferrer");
        toast.success("Opening Stripe Billing Portal to modify plan...");
      } else {
        toast.error(response.data.message || "Failed to open billing portal");
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      toast.error(`Failed to connect to billing service: ${error.message}`);
    }
  };

  // Handle cancel subscription
  const handleCancelSubscription = async (subscription) => {
    // Check if subscription has an ID
    const subscriptionId = subscription?.subscription_id || subscription?.id;

    if (!subscriptionId) {
      toast.error("Unable to find subscription ID");
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to cancel the subscription "${subscription.currentPlan}"? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await Payment.cancelStripeSubscription(subscriptionId);

      if (response.data?.success || response.status === 200) {
        toast.success("Subscription cancelled successfully");
        // Reload the stripe details to reflect the changes
        await loadLocationAndStripeDetails();
      } else {
        toast.error(response.data?.message || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to cancel subscription";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle add subscription button click
  const handleAddSubscription = async () => {
    setShowPlanModal(true);
    setLoadingPlans(true);
    try {
      const response = await Payment.getSubscribePlan();
      if (response.data) {
        let plans = [];

        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          plans = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          plans = response.data.data;
        } else if (response.data.plans && Array.isArray(response.data.plans)) {
          plans = response.data.plans;
        } else if (typeof response.data === "object") {
          plans = [response.data];
        }

        setHiringPlans(plans);
      } else {
        console.warn("No data in response");
        setHiringPlans([]);
      }
    } catch (error) {
      console.error("Error loading hiring plans:", error);
      toast.error("Failed to load hiring plans");
      setHiringPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Handle plan selection
  const handleSelectPlan = async (plan) => {
    const planId = plan?.id;
    const customerId = stripeDetails?.customerId;

    if (!planId) {
      toast.error("Plan ID is missing. Please try another plan.");
      return;
    }

    if (!customerId) {
      toast.error("Customer ID not found. Please ensure Stripe is connected.");
      return;
    }

    setShowPlanModal(false);
    setLoading(true);

    try {
      const result = await Payment.getWelcomeCheckoutURL({
        plan_id: planId,
        customer_stripe_id: customerId,
      });

      const paymentUrl = result?.data?.url?.url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        toast.error("Unable to generate checkout URL");
        setShowPlanModal(true); // Reopen modal on error
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create checkout session";
      toast.error(errorMessage);
      setShowPlanModal(true); // Reopen modal on error
    } finally {
      setLoading(false);
    }
  };

  // Plan Selection Modal Component
  const PlanSelectionModal = () => {
    if (!showPlanModal) return null;

    return (
      <div
        className="fixed z-50 inset-0 overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowPlanModal(false)}
          />

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
            &#8203;
          </span>

          <div className="relative inline-block align-bottom bg-white">
            {/* Modal Header */}
            <div className="bg-primary-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Select a Hiring Plan
                  </h3>
                  <p className="text-sm text-white text-opacity-80 mt-1">
                    Choose the best plan for {location?.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <i className="far fa-times text-2xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              {loadingPlans ? (
                <div className="text-center py-12">
                  <i className="fad fa-circle-notch fa-spin text-4xl text-primary-600 mb-4" />
                  <p className="text-gray-600">
                    Loading plans...
                  </p>
                </div>
              ) : hiringPlans.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {hiringPlans.map((plan, index) => {
                    // Determine billing period from plan name
                    const isAnnual = plan.name
                      ?.toLowerCase()
                      .includes("annual");
                    const isMonthly = plan.name
                      ?.toLowerCase()
                      .includes("monthly");
                    const billingPeriod = isAnnual
                      ? "year"
                      : isMonthly
                      ? "month"
                      : "period";

                    // Determine if it's a default plan
                    const isDefault = plan.is_default === 1;

                    // Get allowed positions
                    const positions = plan.allowed_position || "Unlimited";

                    return (
                      <div
                        key={plan.id || index}
                        className={`relative border-2 rounded-xl p-6 hover:border-primary-500 hover:shadow-lg transition-all duration-200 cursor-pointer ${
                          isDefault
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200"
                        }`}
                        onClick={() => handleSelectPlan(plan)}
                      >
                        {isDefault && (
                          <div className="absolute top-4 right-4">
                            <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-primary-600 text-white">
                              Default
                            </span>
                          </div>
                        )}

                        <div className="text-center mb-6">
                          <h4 className="text-xl font-bold text-gray-900">
                            {plan.name}
                          </h4>
                          <div className="inline-flex items-center justify-center px-4 py-2 bg-primary-100">
                            <i className="far fa-clock text-primary-600" />
                            <span className="text-sm font-medium text-primary-700">
                              Billed {billingPeriod}ly
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-start">
                            <i className="far fa-check-circle text-green-500 mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-700">
                              {positions === "Unlimited" || positions === null
                                ? "Unlimited job positions"
                                : `Up to ${positions} job position${
                                    positions > 1 ? "s" : ""
                                  }`}
                            </span>
                          </div>

                          {plan.stripe_plan_id && (
                            <div className="flex items-start">
                              <i className="far fa-badge-check text-blue-500 mt-1 mr-2 flex-shrink-0" />
                              <span className="text-sm text-gray-700">
                                Stripe verified plan
                              </span>
                            </div>
                          )}

                          {isAnnual && (
                            <div className="flex items-start">
                              <i className="far fa-tags text-purple-500 mt-1 mr-2 flex-shrink-0" />
                              <span className="text-sm text-gray-700">
                                Annual billing - Best value
                              </span>
                            </div>
                          )}
                        </div>

                        <button className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors font-medium">
                          Select {plan.name}
                        </button>

                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Plan ID: {plan.id}</span>
                            {plan.primary_stripe_price_id && (
                              <i
                                className="far fa-shield-check text-green-500"
                                title="Stripe Price ID configured"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="far fa-exclamation-circle text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    No plans available at the moment.
                  </p>
                  <p className="text-sm text-gray-500">
                    Please try again later or contact support.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Stripe Widget Components

  const StripeBillingPortal = ({ customerId }) => {
    const [portalLoading, setPortalLoading] = useState(false);

    const openBillingPortal = async () => {
      setPortalLoading(true);
      try {
        let finalCustomerId = customerId;

        // Fallback mechanism similar to your code
        if (!finalCustomerId) {
          finalCustomerId = location?.company?.stripe_customer_id;
        }

        if (!finalCustomerId && location?.company?.id && location?.id) {
          try {
            const authResponse = await Payment.getStripeCustomerID(
              location.company.id,
              location.id
            );
            finalCustomerId =
              authResponse?.data?.stripe_customer?.stripe_customer_id;
          } catch (fetchError) {
            // Silently handle fetch error
            console.log("Could not fetch customer ID:", fetchError);
          }
        }

        if (!finalCustomerId) {
          toast.error(
            "Unable to retrieve billing information. Please contact support."
          );
          return;
        }

        // Create billing portal session using your API
        const response = await Payment.createBillingPortalSession(
          finalCustomerId
        );

        if (response.data.success && response.data.login_url) {
          // Open billing portal in new tab with security options
          window.open(response.data.login_url, "_blank", "noopener,noreferrer");
          toast.success("Opening Stripe Billing Portal...");
        } else {
          toast.error(
            response.data.message || "Failed to get billing portal URL"
          );
        }
      } catch (error) {
        console.error("Error opening billing portal:", error);
        toast.error(`Failed to connect to billing service: ${error.message}`);
      } finally {
        setPortalLoading(false);
      }
    };

    return (
      <div className="bg-white">
        <h3 className="text-lg font-semibold text-gray-900">
          Stripe Customer Portal
        </h3>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Give customers direct access to manage their billing information,
            download invoices, and update payment methods through Stripe's
            hosted portal.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={openBillingPortal}
              disabled={portalLoading}
              className="flex items-center justify-center px-4 py-2 bg-stripe-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#635bff" }}
            >
              <i className="far fa-external-link mr-2" />
              {portalLoading ? "Opening..." : "Open Billing Portal"}
            </button>

            <button
              onClick={() =>
                window.open(
                  `https://dashboard.stripe.com/customers/${customerId}`,
                  "_blank"
                )
              }
              className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700"
            >
              <i className="far fa-external-link mr-2" />
              View in Stripe Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  const StatCard = ({ title, value, icon, color = "primary", description }) => (
    <div className="bg-white">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900">
            {value}
          </p>
          {description && (
            <p className="text-xs text-gray-500">
              {description}
            </p>
          )}
        </div>
        <div
          className={`p-3 rounded-full bg-${color}-100`}
        >
          <i
            className={`${icon} text-${color}-600`}
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout >
        <div className="text-center">
          <i className="fad fa-circle-notch fa-spin text-4xl text-primary" />
          <p className="mt-4 text-gray-600">
            Loading Stripe details...
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (!location) {
    return (
      <DashboardLayout >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <i className="far fa-exclamation-triangle text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">
              Location not found
            </h3>
            <button
              onClick={() => navigate("/admin/stripe")}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Back to Stripe Management
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout >
      <PlanSelectionModal />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate("/admin/stripe")}
                className="flex items-center text-gray-600"
              >
                <i className="far fa-arrow-left mr-2" />
                Back to Stripe Management
              </button>
              <h2 className="text-2xl font-bold text-gray-900">
                Stripe Details - {location.name}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Comprehensive Stripe integration details for this location
              </p>
            </div>
          </div>
        </div>

        {/* Location Info Card */}
        <div className="bg-white">
          <h3 className="text-lg font-semibold text-gray-900">
            Location Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Location Name
              </p>
              <p className="text-base text-gray-900">
                {location.name}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                Company
              </p>
              <p className="text-base text-gray-900">
                {location.company?.name}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                Address
              </p>
              <p className="text-base text-gray-900">
                {location.address?.address}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                City, State
              </p>
              <p className="text-base text-gray-900">
                {location.address?.city}, {location.address?.state}{" "}
                {location.address?.zip}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Connection Status"
            value={stripeDetails.customerId ? "Connected" : "Not Connected"}
            icon={
              stripeDetails.customerId
                ? "far fa-check-circle"
                : "far fa-times-circle"
            }
            color={stripeDetails.customerId ? "green" : "red"}
            description={
              stripeDetails.customerId
                ? `ID: ${stripeDetails.customerId.substring(0, 20)}...`
                : "No Stripe customer"
            }
          />
          <StatCard
            title="Payment Methods"
            value={
              Array.isArray(stripeDetails.cards)
                ? stripeDetails.cards.length
                : 0
            }
            icon="far fa-credit-card"
            color="blue"
            description="Active payment methods"
          />
          <StatCard
            title="Monthly Amount"
            value={
              stripeDetails.subscriptions[0]?.unitAmount
                ? `$${stripeDetails.subscriptions[0].unitAmount.toFixed(2)}`
                : "$0.00"
            }
            icon="far fa-dollar-sign"
            color="green"
            description={
              stripeDetails.subscriptions[0]?.currency?.toUpperCase() || "USD"
            }
          />
          <StatCard
            title="Next Payment"
            value={stripeDetails.subscriptions[0]?.nextPaymentDate || "N/A"}
            icon="far fa-calendar"
            color="purple"
            description={
              stripeDetails.subscriptions[0]?.isCardExpiry
                ? "Card expiring soon"
                : "Payment scheduled"
            }
          />
        </div>

        {stripeDetails.customerId ? (
          <div className="space-y-8">
            {/* Stripe Widgets Toggle */}
            <div className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Direct Stripe Integration
                  </h3>
                  <p className="text-sm text-gray-600">
                    Connect directly with Stripe widgets for real-time
                    management
                  </p>
                </div>
                <button
                  onClick={() => setShowStripeWidgets(!showStripeWidgets)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    showStripeWidgets
                      ? "bg-primary-500 text-white hover:bg-primary-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {showStripeWidgets
                    ? "Hide Stripe Widgets"
                    : "Show Stripe Widgets"}
                </button>
              </div>
            </div>

            {/* Stripe Widgets Section */}
            {showStripeWidgets && (
              <div className="space-y-6">
                <StripeBillingPortal customerId={stripeDetails.customerId} />
              </div>
            )}

            {/* Payment Methods */}
            <div className="bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Payment Methods
                </h3>
                <button
                  onClick={handleAddPaymentMethod}
                  className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                >
                  <i className="far fa-plus mr-2" />
                  Add Payment Method
                </button>
              </div>
              {Array.isArray(stripeDetails.cards) &&
              stripeDetails.cards.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stripeDetails.cards.map((card, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-blue-50 to-blue-100"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded flex items-center justify-center">
                            <i className="far fa-credit-card text-white text-sm" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {card.card?.brand || "Credit Card"}
                            </h4>
                            <p className="text-sm text-gray-600">
                              •••• •••• •••• {card.card?.last4 || "****"}
                            </p>
                          </div>
                        </div>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Cardholder:
                          </span>
                          <span className="font-medium text-gray-900">
                            {card.billing_details?.name || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Expires:
                          </span>
                          <span className="font-medium text-gray-900">
                            {card.card?.exp_month || "**"}/
                            {card.card?.exp_year || "****"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Email:
                          </span>
                          <span className="font-medium text-gray-900">
                            {card.billing_details?.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Country:
                          </span>
                          <span className="font-medium text-gray-900">
                            {card.billing_details?.address?.country || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex space-x-2">
                          <button
                            onClick={handleUpdatePaymentMethod}
                            className="flex-1 text-primary-600 hover:text-primary-700"
                          >
                            Update
                          </button>
                          <button
                            onClick={() => handleRemovePaymentMethod(card)}
                            className="flex-1 text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="far fa-credit-card text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    No payment methods found for this location.
                  </p>
                  <button
                    onClick={handleAddPaymentMethod}
                    className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                  >
                    <i className="far fa-plus mr-2" />
                    Add Payment Method
                  </button>
                </div>
              )}
            </div>

            {/* Subscription Summary */}
            {stripeDetails.subscriptions.length > 0 && (
              <div className="bg-white">
                <h3 className="text-lg font-semibold text-gray-900">
                  Subscription Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stripeDetails.subscriptions.map((subscription, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-primary-50 to-primary-100"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">
                          {subscription.currentPlan}
                        </h4>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            subscription.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subscription.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Amount:
                          </span>
                          <span className="font-medium text-gray-900">
                            ${subscription.unitAmount.toFixed(2)}{" "}
                            {subscription.currency?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Next Payment:
                          </span>
                          <span className="font-medium text-gray-900">
                            {subscription.nextPaymentDate}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Card Expires:
                          </span>
                          <span
                            className={`font-medium ${
                              subscription.isCardExpiry
                                ? "text-red-600"
                                : "text-gray-900"
                            }`}
                          >
                            {subscription.cardExpiryDate}
                            {subscription.isCardExpiry && (
                              <i className="far fa-exclamation-triangle ml-1 text-red-500" />
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscriptions */}
            <div className="bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Subscription Details
                </h3>
                {stripeDetails.subscriptions.length === 0 && (
                  <button
                    onClick={handleAddSubscription}
                    className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary transition-colors text-sm font-medium"
                  >
                    <i className="far fa-plus mr-2" />
                    Add Subscription
                  </button>
                )}
              </div>
              {Array.isArray(stripeDetails.subscriptions) &&
              stripeDetails.subscriptions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Current Plan
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Currency
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Next Payment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Card Expiry
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {stripeDetails.subscriptions.map(
                        (subscription, index) => {
                          // Extract information from the API response format
                          const planName =
                            subscription?.currentPlan || "Unknown Plan";
                          const amount = subscription?.unitAmount;
                          const currency = subscription?.currency || "USD";
                          const nextPaymentDate = subscription?.nextPaymentDate;
                          const cardExpiryDate = subscription?.cardExpiryDate;
                          const isCardExpiry = subscription?.isCardExpiry;
                          const isActive = subscription?.isActive;

                          return (
                            <tr
                              key={index}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {planName}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    isActive
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {amount ? `$${amount.toFixed(2)}` : "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  {currency?.toUpperCase()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  {nextPaymentDate || "N/A"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  <div>{cardExpiryDate || "N/A"}</div>
                                  {isCardExpiry && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 mt-1">
                                      Expiring Soon
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() =>
                                    handleManageSubscription(subscription)
                                  }
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  Manage
                                </button>
                                {isActive ? (
                                  <button
                                    onClick={() =>
                                      handleCancelSubscription(subscription)
                                    }
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Cancel
                                  </button>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handleManageSubscription(subscription)
                                    }
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Reactivate
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="far fa-sync text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    No active subscriptions found for this location.
                  </p>
                  <button
                    onClick={handleAddSubscription}
                    className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    <i className="far fa-plus mr-2" />
                    Add Subscription
                  </button>
                </div>
              )}
            </div>

            {/* Recent Invoices/Payouts */}
            <div className="bg-white">
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Invoices & Payouts
              </h3>
              {Array.isArray(stripeDetails.invoices) &&
              stripeDetails.invoices.length > 0 ? (
                <div className="space-y-4">
                  {stripeDetails.invoices.map((invoice, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <i className="far fa-file-invoice text-2xl text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Invoice #{invoice.number || invoice.id}
                          </p>
                          <p className="text-sm text-gray-600">
                            {invoice.created
                              ? formatDate(invoice.created)
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            {invoice.amount_paid
                              ? formatCurrency(invoice.amount_paid)
                              : "N/A"}
                          </p>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === "paid"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {invoice.status || "Unknown"}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDownloadInvoice(invoice)}
                          className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100"
                          title="Download Invoice"
                        >
                          <i className="far fa-download" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="far fa-file-invoice text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    No recent invoices or payouts found for this location.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Not Connected State */
          <div className="bg-white">
            <div className="text-center">
              <i className="far fa-plug text-6xl text-gray-400 mb-6" />
              <h3 className="text-xl font-semibold text-gray-900">
                Stripe Not Connected
              </h3>
              <p className="text-gray-600">
                This location is not connected to Stripe. Set up Stripe
                integration to accept payments and manage subscriptions.
              </p>
              <button className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors">
                Connect Stripe Account
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StripeDetails;
